<template>
  <div class="object-attachment">
    <h3>{{ $t("attr_attachment_title") }}</h3>

    <el-table :data="tableData" v-loading="loading" stripe style="width: 100%;">
      <el-table-column
        prop="url"
        width="100"
        align="center"
        :label="$t('attr_attachment_table_file')"
      >
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.isImage"
            style="width: 100px; height: 68px;"
            :src="scope.row.url || scope.row.source"
            :preview-src-list="[scope.row.url || scope.row.source]"
          ></el-image>
          <img v-else src="/img/paper.png" alt="Document icon" />
        </template>
      </el-table-column>

      <el-table-column
        prop="name"
        align="center"
        :label="$t('attr_attachment_table_name')"
      >
        <template slot-scope="scope">
          <el-input
            :class="{
              'object-attachment__control_dublicate': isDublicateName(scope.row)
            }"
            v-model="scope.row.fileShortName"
            :disabled="scope.row.url.length > 0"
            @change="changeFileName(scope.row)"
          >
            <template slot="append">{{ scope.row.fileExt }}</template>
          </el-input>
          <span v-if="isDublicateName(scope.row)">
            {{ $t("attr_attachment_table_name_rule") }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="language"
        align="center"
        :label="$t('attr_attachment_table_language')"
      >
        <template slot-scope="scope">
          <el-select
            v-model="scope.row.language"
            :disabled="scope.row.url.length > 0"
            filterable
            @change="changeFileLang(scope.row)"
          >
            <el-option
              v-for="item in languages"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </template>
      </el-table-column>
    </el-table>

    <div class="object-attachment__actions">
      <div>
        <form>
          <label
            class="el-button object-attachment__actions_item el-button--primary el-button--mini"
            for="object_upload_attachment_files"
          >
            {{ $t("attr_attachment_add_new") }}
          </label>
          <input
            type="file"
            id="object_upload_attachment_files"
            multiple
            @change="handleFiles"
          />
        </form>
      </div>

      <div>
        <el-button class="object-attachment__actions_item" @click="cancel">
          {{ $t("attr_attachment_back") }}
        </el-button>

        <el-button
          class="object-attachment__actions_item"
          type="success"
          :disabled="saveDisabled"
          @click="save"
        >
          {{ $t("attr_attachment_save") }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";

import { MODE_INIT, MODE_EDIT } from "@/domain/mode.types";

import * as attachmentService from "@/domain/object/attachment.service";
import { clone } from "@/domain/system/utils";

export default {
  name: "ObjectAttachment",
  components: {},
  props: {
    loading: Boolean,
    languages: Array
  },
  data() {
    return {
      tableData: [],
      mode: MODE_INIT
    };
  },
  computed: {
    ...mapState({
      attachment: state => state.objectComplexValue.fileList.file,
      currentLanguage: state => state.objectSetting.currentLanguage,
      maxFileSize: state => state.settings.maxFileSize,
      requaredField: state => state.objectComplexValue.requaredField
    }),
    isEdited() {
      return this.mode === MODE_EDIT;
    },
    usedFileNames() {
      return this.tableData.map(t => {
        return {
          guid: t.guid,
          name: `${t.fileShortName}.${t.fileExt}`.toLowerCase()
        };
      });
    },
    isEmptyRequaredField() {
      return this.program.length === 0;
    },
    saveDisabled() {
      return (
        !this.isEdited || this.tableData.some(t => this.isDublicateName(t))
      );
    }
  },
  methods: {
    ...mapActions([]),
    ...mapMutations(["appendFileList", "updateFileItem"]),
    setTableData() {
      this.tableData = [];
      this.$nextTick(function () {
        this.tableData = this.attachment.map(t => clone(t));
      });
    },
    cancel() {
      this.mode = MODE_INIT;
      this.$emit("cancel");
    },
    save() {
      this.mode = MODE_INIT;
      this.$emit("save");
    },
    async handleFiles(e) {
      let bigFiles = [];
      let isAppendedFiles = false;

      if (e.target.files) {
        let files = [...e.target.files];
        for (let i = 0; i < files.length; i++) {
          if (files[i].size <= this.maxFileSize) {
            this.appendFileList(
              await attachmentService.convertFileToAttachmentFile(files[i])
            );
            isAppendedFiles = true;
          } else {
            bigFiles.push(files[i].name);
          }
        }

        if (isAppendedFiles) {
          this.mode = MODE_EDIT;
        }

        if (bigFiles.length) {
          this.$notify({
            title: this.$t("attr_attachment_message_big_size_title"),
            message: this.$t("attr_attachment_message_big_size", {
              files: bigFiles.join(", "),
              maxSize: this.maxFileSize / 1000000
            }),
            type: "error"
          });
        }
      }
    },
    changeFileName(item) {
      this.updateFileItem({
        guid: item.guid,
        fieldName: "fileShortName",
        value: item.fileShortName
      });
    },
    changeFileLang(item) {
      this.updateFileItem({
        guid: item.guid,
        fieldName: "language",
        value: item.language
      });
    },
    isDublicateName(item) {
      if (!item.url) {
        let name = `${item.fileShortName}.${item.fileExt}`.toLowerCase();
        return (
          !item.fileShortName ||
          this.usedFileNames
            .filter(t => t.guid !== item.guid)
            .some(t => t.name === name)
        );
      }
      return false;
    }
  },
  mounted() {
    this.setTableData();
  },
  watch: {
    attachment: function () {
      this.setTableData();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/css/variables.scss";

.object-attachment {
  padding: $component_padding_on_top $component_padding_on_left;
  height: calc(100% - 30px);
  overflow: auto;

  .object-attachment__actions {
    display: flex;
    justify-content: space-between;
    position: sticky;
    bottom: -22px;
    margin-top: 10px;
    padding: 10px 0;
    text-align: right;
    width: 100%;
    background-color: $free_zone_color;
    z-index: 4;
  }

  #object_upload_attachment_files {
    display: none;
  }

  .object-attachment__control_dublicate {
    border: 1px solid $danger_color;
    border-radius: 4px;
  }

  .object-attachment__error {
    padding-top: $component_padding_on_top;
    color: $danger_color;
    font-size: $main_font_size;
  }
}
</style>
